// L05 · The Perfect Circle (SPEC §3/§6, Act II). Second inverted biometric.
// "Draw a perfect circle." We fit a circle to the drawn path (least squares) and score it
// 1 − RMS deviation / radius. Over 97% is *too* perfect — a suspicion event (accused, then
// acquitted: "your hand shook on the final arc"), never a strike. Under 97% passes, and the
// number is shown big because it goes on the Certificate of Humanity (§8).
//
// Geometry is computed on-device; only the score number and verdict leave the level.
// Traits: `precise` (>90) / `beautifully flawed` (<70).

import { el } from "@/ui/chrome.ts";
import { runLevel, promptEl } from "@/levels/util.ts";
import {
  circularity,
  CIRCLE_TOO_PERFECT,
  CIRCLE_PRECISE,
  CIRCLE_FLAWED,
  type Point,
} from "@/engine/biometrics.ts";
import { L05 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

const W = 260;
const H = 220;

export const L05: Level = {
  id: "L05",
  act: 2,
  title: "The Perfect Circle",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      host.append(promptEl("Draw a perfect circle."));

      const canvas = el("canvas", "pad pad--draw");
      const dpr = Math.min(window.devicePixelRatio || 1, 3);
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      canvas.setAttribute("aria-label", "Drawing area: draw a circle");
      host.append(canvas);

      const hint = el("div", "pad__hint");
      hint.textContent = V.hint;
      host.append(hint);

      const ctx2d = canvas.getContext("2d")!;
      ctx2d.scale(dpr, dpr);
      const accent = () => getComputedStyle(canvas).getPropertyValue("--accent").trim() || "#4285F4";
      const fg = () => getComputedStyle(canvas).getPropertyValue("--fg").trim() || "#1F2933";

      let points: Point[] = [];
      let drawing = false;
      let revealTimer = 0;

      const clear = () => {
        ctx2d.clearRect(0, 0, W, H);
      };
      const strokeTo = (p: Point) => {
        const prev = points[points.length - 2];
        if (!prev) return;
        ctx2d.strokeStyle = accent();
        ctx2d.lineWidth = 2.5;
        ctx2d.lineCap = "round";
        ctx2d.lineJoin = "round";
        ctx2d.beginPath();
        ctx2d.moveTo(prev.x, prev.y);
        ctx2d.lineTo(p.x, p.y);
        ctx2d.stroke();
      };
      const at = (e: PointerEvent): Point => {
        const rect = canvas.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };

      const onDown = (e: PointerEvent) => {
        e.preventDefault();
        drawing = true;
        points = [at(e)];
        clear();
        try {
          canvas.setPointerCapture(e.pointerId);
        } catch {
          /* capture is best-effort; drawing still works without it */
        }
      };
      const onMove = (e: PointerEvent) => {
        if (!drawing) return;
        points.push(at(e));
        strokeTo(points[points.length - 1]!);
      };
      const onUp = () => {
        if (!drawing) return;
        drawing = false;
        finalize();
      };

      const finalize = () => {
        const result = circularity(points);
        if (!result) {
          // Not enough of a loop — invite a redraw in-voice, keep the level open.
          hint.textContent = V.incomplete;
          return;
        }
        const { score, fit } = result;

        // Draw the number big — this is the certificate number.
        clear();
        ctx2d.strokeStyle = accent();
        ctx2d.globalAlpha = 0.35;
        ctx2d.lineWidth = 2;
        ctx2d.beginPath();
        ctx2d.arc(fit.cx, fit.cy, fit.r, 0, 2 * Math.PI);
        ctx2d.stroke();
        ctx2d.globalAlpha = 1;
        ctx2d.fillStyle = fg();
        // Canvas font strings cannot read CSS custom properties — use a concrete stack so
        // the certificate number actually renders big (Act II is system UI regardless).
        ctx2d.font = '600 46px "Roboto", -apple-system, system-ui, sans-serif';
        ctx2d.textAlign = "center";
        ctx2d.textBaseline = "middle";
        ctx2d.fillText(`${Number.isInteger(score) ? score : score.toFixed(1)}%`, W / 2, H / 2);

        const traits: string[] = [];
        if (score > CIRCLE_PRECISE) traits.push("precise");
        else if (score < CIRCLE_FLAWED) traits.push("beautifully flawed");

        let line: string;
        let suspicionEvent = false;
        if (score > CIRCLE_TOO_PERFECT) {
          line = V.tooPerfect(score);
          suspicionEvent = true;
        } else if (score > CIRCLE_PRECISE) {
          line = V.precise(score);
        } else if (score < CIRCLE_FLAWED) {
          line = V.flawed(score);
        } else {
          line = V.fair(score);
        }

        // Let the big number breathe before the verdict beat covers the body.
        revealTimer = window.setTimeout(
          () => done({ pass: true, suspicionEvent, line, traits, data: { circleScore: score } }),
          ctx.reducedMotion ? 250 : 700,
        );
      };

      canvas.addEventListener("pointerdown", onDown);
      canvas.addEventListener("pointermove", onMove);
      canvas.addEventListener("pointerup", onUp);
      canvas.addEventListener("pointercancel", onUp);

      return () => window.clearTimeout(revealTimer);
    });
  },
};
